import React, { useMemo } from 'react'
import { Col, Row, Stack } from 'react-bootstrap'
import { useSessionAvailability, useZocksAuth } from '../../components/ScopedBlock/hooks'
import { AdhocTool } from '../../components/MeetingTools/AdhocTool'
import { IntegrationServiceId } from '../../common/integrations'
import { MeetingProviderAccountIntegrationConfig } from '../../models/Account'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'
import { useGetMeQuery } from '../../store/user/user'
import { SharedPhoneTool } from '../../components/MeetingTools/SharedPhoneTool'
import { DedicatedPhoneIntegrationTool } from '../../components/MeetingTools/DedicatedPhoneIntegrationTool'
import { InPersonTool } from '../../components/MeetingTools/InPersonTool'
import { ReactComponent as LivePhoneIcon } from '../../assets/images/public-cloud-call-1.svg'
import { CollapsiblePanel } from '../../components/CollapsiblePanel/CollapsiblePanel'
import { useIntegrations } from '../Integration/useIntegrations'

export const LiveHeaderRow: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const { data: userData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const { hasScope } = useZocksAuth()
  const hasStartSessionScope = hasScope('**', 'room_session:start', ['room_session:admin', 'room:admin'])
  const { available: sessionAvailable } = useSessionAvailability()
  const { isIntegrationAllowed } = useIntegrations()

  const hasPhoneIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === IntegrationServiceId.PHONE && i.state === 'live'))
  }, [userData])

  const phoneConfig = useMemo(() => {
    if (!account?.data?.effectiveConfig?.integrations) {
      return null
    }
    return (
      (account.data.effectiveConfig.integrations.find(
        (integration) => integration.type === 'meeting_provider' && integration.serviceId === 'phone',
      ) as MeetingProviderAccountIntegrationConfig) ?? null
    )
  }, [account])

  const meetingTools = [
    { id: 'inPerson', icon: <i className='bi bi-laptop' />, body: <InPersonTool /> },
    ...(phoneConfig?.sharedPhoneLineEnabled && isIntegrationAllowed(IntegrationServiceId.PHONE)
      ? [{ id: 'sharedPhone', icon: <LivePhoneIcon width={'1rem'} height={'1rem'} className='me-1' />, body: <SharedPhoneTool phoneConfig={phoneConfig} /> }]
      : []),
    ...(!phoneConfig?.sharedPhoneLineEnabled && isIntegrationAllowed(IntegrationServiceId.PHONE) && !hasPhoneIntegration
      ? [{ id: 'dedicatedPhone', icon: <i className='bi bi-telephone' />, body: <DedicatedPhoneIntegrationTool /> }]
      : []),
    { id: 'adhoc', icon: <i className='bi bi-broadcast' />, body: <AdhocTool /> },
  ].slice(0, 3)

  return (
    <CollapsiblePanel
      id='LiveMeetings-MeetingTools'
      header={<h6>Meeting Tools</h6>}
      headerWhileCollapsed={
        <>
          <h6>Meeting Tools</h6>
          <Stack direction='horizontal' gap={1} className='tool-icons'>
            {meetingTools.map((tool) => (
              <div key={tool.id} className='meeting-tool-icon-wrapper'>
                {tool.icon}
              </div>
            ))}
          </Stack>
        </>
      }
      defaultCollapsedState={!hasStartSessionScope || !sessionAvailable ? 'collapsed' : 'expanded'}
    >
      <Row className='p-0'>
        {meetingTools.map((tool) => (
          <Col key={tool.id} xs={12} lg className='upcoming-integration-box'>
            {tool.body}
          </Col>
        ))}
      </Row>
    </CollapsiblePanel>
  )
}
